// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import QtyField, { QtyFieldStyles } from "@sc/App/Components/Qty/QtyField";
import { mergeWithCss } from "@sc/XDL/Utilities";

import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    // XNG-Change
    extendedStyles?: QtyFieldStyles;
    showLabel?: boolean;
    showDisabled?: boolean;
}

export interface ProductQuantityStyles {
    qtyFieldStyles?: QtyFieldStyles;
}

export const productQuantityStyles: ProductQuantityStyles = {};

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, isVariantParent, detail, minimumOrderQty = 1 },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory, pricing, inventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showLabel = false,
    showDisabled,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productQuantityStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [minQtyState, setMinQty] = useState(1);
    const [maxQtyState, setMaxQty] = useState(9999);
    const [searchTimeoutId, setSearchTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);

    const showQtyTooltip =
        (!!((minimumOrderQty && minimumOrderQty > 1) || (detail?.multipleSaleQty && detail?.multipleSaleQty > 1)));

    // Xng-Changes
    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, 1, (detail?.multipleSaleQty && detail?.multipleSaleQty > 1) ? detail.multipleSaleQty : 1);
        const qty = Math.max(minQty, qtyOrdered);
        setProductQty(qty.toString());
        setMinQty(minQty);
    }, [minimumOrderQty]);

    useEffect(() => {
        if (detail?.multipleSaleQty && detail?.multipleSaleQty > 1) {
            const maxQty = 9999 - (9999 % detail.multipleSaleQty);
            setMaxQty(maxQty);
        }
    }, [detail?.multipleSaleQty]);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        // Xng-Changes
        setProductQty(`${Math.max(minimumOrderQty || 1, qtyOrdered) || 1}`);
    }, [qtyOrdered]);

    const disabled = (!hasEnoughInventory && !failedToLoadInventory) || (!canAddToCart && !isVariantParent);

    if (disabled && !showDisabled) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement> | string) => {
        // XNG-Change: accepting string qty values
        let  value = typeof event === "string" ? event : event.target.value;
        const quantityValue = Number(value);

        setProductQty(value);
        if (typeof searchTimeoutId === "number") {
            clearTimeout(searchTimeoutId);
        }
        // Add some timeout when user is typing
        setSearchTimeoutId(
            setTimeout(() => {
                 if (detail?.multipleSaleQty && detail?.multipleSaleQty > 1 && quantityValue % detail.multipleSaleQty > 0) {
                    value = (Math.ceil(quantityValue / detail.multipleSaleQty) * (detail.multipleSaleQty)).toString();
                }
                if (Number(value) < minQtyState) {
                    value = `${minQtyState}`;
                }
                setProductQty(value);
                if (!onQtyOrderedChanged) {
                    logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
                    return;
                }
        
                const possibleNumber = Number(value);
                if (!Number.isNaN(possibleNumber)) {
                    onQtyOrderedChanged(possibleNumber);
                }
            }, 500),
        );
    };

    // Xng-Changes
    const blurHandler = () => {
        if (Number(productQty) < minQtyState) {
            setProductQty(`${minQtyState}`);
        }
    };

    // XNG-Change: replcaing TextFiled by QtyField component
    return (
        <>
            <QtyField
                label={labelOverride ?? translate("QTY")}
                value={productQty}
                type="qty"
                min={minQtyState}
                disabled={disabled}
                max={maxQtyState}
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                onBlur={blurHandler}
                showLabel={showLabel}
                extendedStyles={styles.qtyFieldStyles}
                data-test-selector="product_qtyOrdered"
                minimumOrderQty={minimumOrderQty}
                multipleSaleQty={detail?.multipleSaleQty}
                showMultipleSaleQtyTooltip={showQtyTooltip}
            />
        </>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
